import { useState, useEffect } from "react";
import type { ParsedQuestionDraft } from "@/types/questions";
import type { Difficulty, OptionLetter } from "@/types/database";

interface Props {
  initial: ParsedQuestionDraft;
  onChange: (draft: ParsedQuestionDraft) => void;
  onRemove?: () => void;
}

export default function QuestionEditor({ initial, onChange, onRemove }: Props) {
  const [d, setD] = useState<ParsedQuestionDraft>(initial);
  useEffect(() => onChange(d), [d, onChange]);

  function update<K extends keyof ParsedQuestionDraft>(key: K, val: ParsedQuestionDraft[K]) {
    setD((prev) => ({ ...prev, [key]: val }));
  }

  return (
    <div className={`card p-4 space-y-3 ${d.valid ? "" : "ring-2 ring-amber-500"}`}>
      <div className="flex items-center justify-between">
        <span className={d.valid ? "pill-ok" : "pill-warn"}>
          Q{d.position} · {d.valid ? "ready" : `needs review (${d.issues.length})`}
        </span>
        {onRemove && (
          <button onClick={onRemove} className="text-xs text-rose-600 hover:underline">Remove</button>
        )}
      </div>

      <textarea
        className="w-full p-2 rounded-lg border border-steel-200 bg-white text-steel-900"
        rows={2}
        value={d.question_text}
        onChange={(e) => update("question_text", e.target.value)}
        placeholder="Question text"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {(["A","B","C","D"] as const).map((L) => {
          const optKey = (`option_${L.toLowerCase()}`) as "option_a" | "option_b" | "option_c" | "option_d";
          return (
          <label key={L} className="flex items-center gap-2">
            <span className="font-display w-6 text-center">{L}</span>
            <input
              className="flex-1 p-2 rounded-lg border border-steel-200 bg-white text-steel-900"
              value={d[optKey] ?? ""}
              onChange={(e) => update(optKey, e.target.value)}
              placeholder={`Option ${L}`}
            />
            <input
              type="radio"
              name={`correct-${d.position}`}
              checked={d.correct_option === L}
              onChange={() => update("correct_option", L as OptionLetter)}
            />
          </label>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input
          className="p-2 rounded-lg border border-steel-200 bg-white text-steel-900"
          value={d.category ?? ""}
          onChange={(e) => update("category", e.target.value || null)}
          placeholder="Category"
        />
        <select
          className="p-2 rounded-lg border border-steel-200 bg-white text-steel-900"
          value={d.difficulty}
          onChange={(e) => update("difficulty", e.target.value as Difficulty)}
        >
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
        <input
          className="p-2 rounded-lg border border-steel-200 bg-white text-steel-900"
          value={d.code_reference ?? ""}
          onChange={(e) => update("code_reference", e.target.value || null)}
          placeholder="Code reference"
        />
      </div>

      <textarea
        className="w-full p-2 rounded-lg border border-steel-200 bg-white text-steel-900"
        rows={2}
        value={d.explanation ?? ""}
        onChange={(e) => update("explanation", e.target.value || null)}
        placeholder="Explanation (shown to students after answer if enabled)"
      />

      {!d.valid && d.issues.length > 0 && (
        <ul className="text-xs text-amber-700 list-disc pl-5">
          {d.issues.map((i) => <li key={i}>{i}</li>)}
        </ul>
      )}
    </div>
  );
}
